import { collection, doc, getDoc, setDoc, getDocs, query, where, orderBy, limit, Timestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getUserById, updateUserData, logAdminActivity } from '../firebase/helpers';
import { verifyAdminAccess } from './adminService';
import { VIP_CONFIG } from './optimizationService';

// Knife calculation is implemented inline

const AUTO_KNIFE_TRIGGERS_COLLECTION = 'autoKnifeTriggers';
export const USER_TASK_PROGRESS_COLLECTION = 'userTaskProgress';

/**
 * Get maximum task count for VIP level (tasks per set)
 */
export const getMaxTasksForVIP = (vipLevel) => {
  const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
  return config.tasksPerSet || config.tasks;
};

/**
 * Get maximum sets for VIP level
 */
export const getMaxSetsForVIP = (vipLevel) => {
  const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
  return config.sets || config.optimizations;
};

/**
 * Get list of available tasks for VIP level
 */
export const getAvailableTasksForVIP = (vipLevel) => {
  const maxTasks = getMaxTasksForVIP(vipLevel);
  return Array.from({ length: maxTasks }, (_, i) => i + 1);
};

/**
 * Get list of available sets for VIP level
 */
export const getAvailableSetsForVIP = (vipLevel) => {
  const maxSets = getMaxSetsForVIP(vipLevel);
  return Array.from({ length: maxSets }, (_, i) => i + 1);
};

/**
 * Calculate task number from set and task within set
 */
export const calculateTaskNumber = (setNumber, taskInSet, vipLevel) => {
  const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
  const tasksPerSet = config.tasksPerSet || config.tasks;
  return ((setNumber - 1) * tasksPerSet) + taskInSet;
};

/**
 * Calculate set number and task within set from global task number
 */
export const calculateSetAndTask = (taskNumber, vipLevel) => {
  const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
  const tasksPerSet = config.tasksPerSet || config.tasks;
  const setNumber = Math.ceil(taskNumber / tasksPerSet);
  const taskInSet = ((taskNumber - 1) % tasksPerSet) + 1;
  return { setNumber, taskInSet };
};

/**
 * Create a new auto knife trigger
 * @param {Object} triggerData - { setNumber, taskThreshold, knifeAmount, mode, userIds, vipLevel }
 */
export const createAutoKnifeTrigger = async (triggerData) => {
  try {
    const adminInfo = await verifyAdminAccess();

    const { setNumber, taskThreshold, knifeAmount, mode, userIds = [], vipLevel, taskNumber } = triggerData;

    // Support both new set-based and legacy task-based triggers
    let finalSetNumber = null;
    let finalTaskNumber = null;
    let finalThreshold = null;

    if (vipLevel && setNumber !== undefined) {
      // New set-based trigger
      const maxSets = getMaxSetsForVIP(vipLevel);
      const maxTasksPerSet = getMaxTasksForVIP(vipLevel);
      
      if (!setNumber || setNumber < 1 || setNumber > maxSets) {
        throw new Error(`Set number must be between 1 and ${maxSets} for ${vipLevel}`);
      }
      if (!taskThreshold || taskThreshold < 1 || taskThreshold > maxTasksPerSet) {
        throw new Error(`Task threshold must be between 1 and ${maxTasksPerSet} for ${vipLevel} Set ${setNumber}`);
      }
      
      finalSetNumber = parseInt(setNumber);
      finalThreshold = parseInt(taskThreshold);
      finalTaskNumber = calculateTaskNumber(finalSetNumber, finalThreshold, vipLevel);
    } else if (taskNumber !== undefined) {
      // Legacy task-based trigger (backward compatibility)
      finalTaskNumber = parseInt(taskNumber);
      finalThreshold = parseInt(triggerData.threshold || taskThreshold);
      
      if (vipLevel) {
        const maxTasks = getMaxTasksForVIP(vipLevel);
        if (finalTaskNumber > maxTasks) {
          throw new Error(`Task number cannot exceed ${maxTasks} for ${vipLevel}`);
        }
        if (finalThreshold < 1 || finalThreshold > maxTasks) {
          throw new Error(`Threshold must be between 1 and ${maxTasks} for ${vipLevel}`);
        }
      } else {
        if (finalTaskNumber > 55) {
          throw new Error('Task number cannot exceed 55');
        }
        if (finalThreshold < 1 || finalThreshold > 55) {
          throw new Error('Threshold must be between 1 and 55');
        }
      }
    } else {
      throw new Error('Either setNumber (with vipLevel) or taskNumber must be provided');
    }
    
    if (!knifeAmount || knifeAmount <= 0) {
      throw new Error('Knife amount must be greater than 0');
    }
    if (mode === 'individual' && (!userIds || userIds.length === 0)) {
      throw new Error('Please select at least one user for individual mode');
    }
    if (vipLevel && !VIP_CONFIG[vipLevel]) {
      throw new Error(`Invalid VIP level: ${vipLevel}`);
    }

    // Create trigger document
    const triggerRef = doc(collection(db, AUTO_KNIFE_TRIGGERS_COLLECTION));
    const triggerId = triggerRef.id;

    const triggerDoc = {
      id: triggerId,
      taskNumber: finalTaskNumber, // Global task number for compatibility
      setNumber: finalSetNumber, // Set number (new)
      taskThreshold: finalThreshold, // Task threshold within set (new)
      threshold: finalThreshold, // Legacy support
      knifeAmount: parseFloat(knifeAmount),
      mode: mode, // 'bulk' or 'individual'
      userIds: mode === 'bulk' ? [] : userIds,
      vipLevel: vipLevel, // Store VIP level for filtering
      enabled: true,
      appliedCount: 0,
      totalAmountApplied: 0,
      createdAt: Timestamp.now(),
      createdBy: adminInfo.adminId,
      createdByEmail: adminInfo.adminEmail,
      updatedAt: Timestamp.now()
    };

    await setDoc(triggerRef, triggerDoc);

    // Log admin activity
    const description = vipLevel && finalSetNumber
      ? `Created auto knife trigger: ${vipLevel} - Set ${finalSetNumber} - Task ${finalThreshold}/${getMaxTasksForVIP(vipLevel)}, Amount ${knifeAmount} USDT, Mode: ${mode}`
      : `Created auto knife trigger: Task ${finalTaskNumber}, Threshold ${finalThreshold}, Amount ${knifeAmount} USDT, Mode: ${mode}${vipLevel ? `, VIP: ${vipLevel}` : ''}`;
    
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'createAutoKnifeTrigger',
      targetUserId: null,
      targetUserEmail: null,
      changes: triggerDoc,
      description: description
    });

    return { success: true, triggerId, trigger: triggerDoc };
  } catch (error) {
    console.error('Error creating auto knife trigger:', error);
    throw error;
  }
};

/**
 * Get all auto knife triggers
 */
export const getAllAutoKnifeTriggers = async () => {
  try {
    await verifyAdminAccess();
    const triggersRef = collection(db, AUTO_KNIFE_TRIGGERS_COLLECTION);
    const q = query(triggersRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const triggers = [];
    snapshot.forEach((doc) => {
      triggers.push(doc.data());
    });
    
    return triggers;
  } catch (error) {
    console.error('Error getting auto knife triggers:', error);
    throw error;
  }
};

/**
 * Update auto knife trigger
 */
export const updateAutoKnifeTrigger = async (triggerId, updates) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    const triggerRef = doc(db, AUTO_KNIFE_TRIGGERS_COLLECTION, triggerId);
    await updateDoc(triggerRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });

    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'updateAutoKnifeTrigger',
      targetUserId: null,
      targetUserEmail: null,
      changes: updates,
      description: `Updated auto knife trigger: ${triggerId}`
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating auto knife trigger:', error);
    throw error;
  }
};

/**
 * Delete auto knife trigger
 */
export const deleteAutoKnifeTrigger = async (triggerId) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    const triggerRef = doc(db, AUTO_KNIFE_TRIGGERS_COLLECTION, triggerId);
    await deleteDoc(triggerRef);

    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'deleteAutoKnifeTrigger',
      targetUserId: null,
      targetUserEmail: null,
      changes: { triggerId },
      description: `Deleted auto knife trigger: ${triggerId}`
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting auto knife trigger:', error);
    throw error;
  }
};

/**
 * Get today's date key (YYYY-MM-DD format)
 */
const getTodayKey = () => {
  const now = new Date();
  return now.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};

/**
 * Reset user's daily task progress to task 1 in current set
 * This is called automatically when a new day is detected
 */
export const resetDailyTaskProgress = async (userId, vipLevel) => {
  try {
    const todayKey = getTodayKey();
    const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (!progressDoc.exists()) {
      return { reset: false, reason: 'No progress found' };
    }
    
    const progress = progressDoc.data();
    const lastResetDate = progress.lastResetDate || null;
    
    // Check if already reset today
    if (lastResetDate === todayKey) {
      return { reset: false, reason: 'Already reset today' };
    }
    
    // Get user's current set from position
    const position = await getUserCurrentPosition(userId, vipLevel).catch(() => null);
    const currentSet = position?.currentSet || 1;
    
    // Reset all tasks in the current set to 0 optimizations
    // But preserve knife records and other metadata
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    const updates = {
      lastResetDate: todayKey,
      lastResetAt: Timestamp.now(),
      knifeAppliedToday: false, // Reset daily knife flag
      updatedAt: Timestamp.now()
    };
    
    // Reset task progress for current set only
    for (let taskInSet = 1; taskInSet <= config.tasksPerSet; taskInSet++) {
      const globalTaskNum = calculateTaskNumber(currentSet, taskInSet, vipLevel);
      const taskKey = `task${globalTaskNum}`;
      const taskData = progress[taskKey] || {};
      
      // Reset optimizations but preserve knife records
      updates[taskKey] = {
        ...taskData,
        optimizations: 0, // Reset to 0
        setNumber: currentSet,
        taskInSet: taskInSet,
        dailyResetAt: Timestamp.now(),
        // Preserve knife information (but reset daily flag)
        knifeApplied: false, // Reset daily knife flag - triggers can apply again today
        knifeAppliedToday: false, // New field to track daily application
        knifeAmount: taskData.knifeAmount || 0, // Keep historical amount
        appliedAt: taskData.appliedAt || null // Keep historical timestamp
      };
    }
    
    await updateDoc(progressRef, updates);
    
    // Create notification for user about daily reset
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: userId,
        type: 'info',
        title: 'Daily Reset Complete',
        message: `Your Set ${currentSet} progress has been reset to Task 1. Auto knife triggers remain active and will apply when you reach the specified task threshold.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating reset notification:', notifError);
    }
    
    console.log(`✅ Daily reset completed for user ${userId}: Set ${currentSet} reset to Task 1`);
    
    return {
      reset: true,
      currentSet,
      todayKey,
      message: `Set ${currentSet} reset to Task 1`
    };
  } catch (error) {
    console.error('Error resetting daily task progress:', error);
    throw error;
  }
};

/**
 * Check if daily reset is needed and perform it
 */
export const checkAndResetDailyProgress = async (userId, vipLevel) => {
  try {
    const todayKey = getTodayKey();
    const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (!progressDoc.exists()) {
      return { resetNeeded: false, reset: false };
    }
    
    const progress = progressDoc.data();
    const lastResetDate = progress.lastResetDate || null;
    
    // If last reset was not today, reset is needed
    if (lastResetDate !== todayKey) {
      return await resetDailyTaskProgress(userId, vipLevel);
    }
    
    return { resetNeeded: false, reset: false, alreadyReset: true };
  } catch (error) {
    console.error('Error checking daily reset:', error);
    return { resetNeeded: false, reset: false, error: error.message };
  }
};

/**
 * Get user's task progress
 * Supports dynamic task counts based on VIP level
 * Now includes automatic daily reset check
 */
export const getUserTaskProgress = async (userId, vipLevel = null) => {
  try {
    const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
    const snapshot = await getDoc(progressRef);
    
    // Get user to determine VIP level if not provided
    if (!vipLevel) {
      const user = await getUserById(userId);
      vipLevel = user?.vipLevel || 'VIP1';
    }
    
    const maxTasks = getMaxTasksForVIP(vipLevel);
    
    if (snapshot.exists()) {
      const data = snapshot.data();
      
      // Check if daily reset is needed
      const todayKey = getTodayKey();
      const lastResetDate = data.lastResetDate || null;
      
      if (lastResetDate !== todayKey) {
        // Reset is needed - perform it
        await checkAndResetDailyProgress(userId, vipLevel);
        // Re-fetch after reset
        const updatedSnapshot = await getDoc(progressRef);
        const updatedData = updatedSnapshot.exists() ? updatedSnapshot.data() : data;
        
        // Ensure all tasks up to maxTasks are initialized
        const progress = { ...updatedData };
        for (let i = 1; i <= maxTasks; i++) {
          const taskKey = `task${i}`;
          if (!progress[taskKey]) {
            progress[taskKey] = { 
              optimizations: 0, 
              knifeApplied: false, 
              knifeAmount: 0, 
              appliedAt: null,
              knifeAppliedToday: false
            };
          }
        }
        return progress;
      }
      
      // Ensure all tasks up to maxTasks are initialized
      const progress = { ...data };
      for (let i = 1; i <= maxTasks; i++) {
        const taskKey = `task${i}`;
        if (!progress[taskKey]) {
          progress[taskKey] = { 
            optimizations: 0, 
            knifeApplied: false, 
            knifeAmount: 0, 
            appliedAt: null,
            knifeAppliedToday: false
          };
        }
      }
      return progress;
    }
    
    // Initialize with default progress for all tasks
    const defaultProgress = {
      userId: userId,
      vipLevel: vipLevel,
      lastResetDate: getTodayKey(),
      knifeAppliedToday: false,
      updatedAt: Timestamp.now()
    };
    
    for (let i = 1; i <= maxTasks; i++) {
      defaultProgress[`task${i}`] = { 
        optimizations: 0, 
        knifeApplied: false, 
        knifeAmount: 0, 
        appliedAt: null,
        removedAt: null,
        commissionEarned: 0,
        knifeAppliedToday: false
      };
    }
    
    await setDoc(progressRef, defaultProgress);
    return defaultProgress;
  } catch (error) {
    console.error('Error getting user task progress:', error);
    throw error;
  }
};

/**
 * Update user's task optimization count
 * FIXED: Validates task count to prevent exceeding max tasks per set
 */
export const updateUserTaskOptimization = async (userId, taskNumber, increment = 1) => {
  try {
    // Get user to determine VIP level and validate
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }
    
    const vipLevel = user.vipLevel || 'VIP1';
    
    // Check and perform daily reset if needed (before updating progress)
    await checkAndResetDailyProgress(userId, vipLevel);
    
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    const maxTasksPerSet = config.tasksPerSet || config.tasks;
    const totalTasks = config.totalTasks || (config.sets * maxTasksPerSet);
    
    // Calculate which set this task belongs to
    const { setNumber, taskInSet } = calculateSetAndTask(taskNumber, vipLevel);
    
    // Validate task number
    if (taskNumber > totalTasks) {
      console.warn(`⚠️ Task ${taskNumber} exceeds max tasks (${totalTasks}) for ${vipLevel}. Correcting...`);
      return { success: false, error: `Task ${taskNumber} exceeds maximum tasks (${totalTasks}) for ${vipLevel}` };
    }
    
    const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
    const taskKey = `task${taskNumber}`;
    
    // Get current progress (after potential reset)
    const currentProgress = await getUserTaskProgress(userId, vipLevel);
    const currentCount = currentProgress[taskKey]?.optimizations || 0;
    const newCount = currentCount + increment;
    
    // CRITICAL FIX: Validate that task count doesn't exceed max tasks per set
    // The optimizations count should represent tasks completed, not exceed maxTasksPerSet
    // If somehow it does, cap it at maxTasksPerSet
    const validatedCount = Math.min(newCount, maxTasksPerSet);
    
    if (newCount > maxTasksPerSet) {
      console.warn(`🔧 CORRECTING: Task ${taskNumber} (Set ${setNumber}, Task ${taskInSet}) count ${newCount} exceeds max ${maxTasksPerSet}. Setting to ${validatedCount}.`);
    }
    
    // Update progress with validated count
    await updateDoc(progressRef, {
      [taskKey]: {
        ...currentProgress[taskKey],
        optimizations: validatedCount,
        setNumber: setNumber,
        taskInSet: taskInSet,
        lastUpdated: Timestamp.now()
      },
      updatedAt: Timestamp.now()
    });
    
    return { 
      success: true, 
      newCount: validatedCount, 
      previousCount: currentCount,
      corrected: newCount > maxTasksPerSet,
      setNumber,
      taskInSet
    };
  } catch (error) {
    console.error('Error updating user task optimization:', error);
    throw error;
  }
};

/**
 * Check and apply auto knife if threshold reached
 * This is called after each task completion
 * Supports both set-based and legacy task-based triggers
 */
export const checkAndApplyAutoKnife = async (userId, taskNumber) => {
  try {
    // Get user data
    const user = await getUserById(userId);
    if (!user) {
      return { applied: false, reason: 'User not found' };
    }

    // Check if user already has negative balance
    if (user.hasNegativeBalance || (user.balances?.negative && user.balances.negative < 0)) {
      return { applied: false, reason: 'User already has negative balance' };
    }

    // ACTIVITY-BASED APPLICATION: Track activity but don't block application
    // Knife applies regardless of online/offline status for guaranteed application
    try {
      const { getUserActivityStatus, trackUserActivity } = await import('./userActivityService');
      const activity = await getUserActivityStatus(userId);
      
      // Track activity for monitoring purposes (non-blocking)
      if (activity.status === 'online' || activity.status === 'recently_active') {
        console.log(`✅ User ${userId} is ${activity.status}. Knife will apply immediately.`);
      } else {
        console.log(`⏳ User ${userId} is ${activity.status}. Knife will apply when threshold reached (works offline too).`);
      }
      
      // Track this activity (non-blocking)
      trackUserActivity(userId, 'knife_check').catch(err => {
        console.warn('Activity tracking failed (non-critical):', err);
      });
    } catch (activityError) {
      // If activity service fails, proceed anyway (fail-safe)
      console.warn('Activity check failed, proceeding with knife application:', activityError);
    }

    // Get user's VIP level and current progress
    const vipLevel = user.vipLevel || 'VIP1';
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    const maxTasksPerSet = getMaxTasksForVIP(vipLevel);
    const totalTasks = config.totalTasks || (config.sets * config.tasksPerSet);
    
    // Validate task number
    if (taskNumber > totalTasks) {
      return { applied: false, reason: `Task ${taskNumber} exceeds maximum tasks (${totalTasks}) for ${vipLevel}` };
    }
    
    // Calculate set and task within set
    const { setNumber, taskInSet } = calculateSetAndTask(taskNumber, vipLevel);
    
    // Check and perform daily reset if needed
    await checkAndResetDailyProgress(userId, vipLevel);
    
    const progress = await getUserTaskProgress(userId, vipLevel);
    const taskKey = `task${taskNumber}`;
    const currentOptimizations = progress[taskKey]?.optimizations || 0;
    const todayKey = getTodayKey();

    // Check if knife already applied today for this task (daily reset allows re-application)
    if (progress[taskKey]?.knifeAppliedToday === true) {
      return { applied: false, reason: 'Knife already applied today for this task' };
    }
    
    // Check if user already has knife applied today (from any task)
    if (progress.knifeAppliedToday === true) {
      return { applied: false, reason: 'Knife already applied today' };
    }

    // Get all active triggers - check both set-based and task-based
    const triggersRef = collection(db, AUTO_KNIFE_TRIGGERS_COLLECTION);
    const q = query(
      triggersRef,
      where('enabled', '==', true)
    );
    const snapshot = await getDocs(q);
    
    // Send notification if user is approaching threshold (2 tasks away)
    // This is done before checking triggers to give users a heads up
    try {
      for (const triggerDoc of snapshot.docs) {
        const trigger = triggerDoc.data();
        if (trigger.vipLevel !== vipLevel) continue;
        
        if (trigger.setNumber !== undefined && trigger.taskThreshold !== undefined) {
          if (trigger.setNumber === setNumber) {
            const tasksRemaining = trigger.taskThreshold - taskInSet;
            if (tasksRemaining === 2) {
              // User is 2 tasks away - send warning notification
              const notificationRef = doc(collection(db, 'notifications'));
              await setDoc(notificationRef, {
                userId: userId,
                type: 'warning',
                title: '⚠️ Approaching Auto Knife Threshold',
                message: `You're 2 tasks away from auto knife application at Task ${trigger.taskThreshold} in Set ${trigger.setNumber}. Complete ${tasksRemaining} more tasks to trigger.`,
                read: false,
                createdAt: Timestamp.now()
              });
            }
          }
        }
      }
    } catch (notifError) {
      console.error('Error sending approach notification:', notifError);
    }

    let applicableTrigger = null;

    // Check each trigger
    for (const triggerDoc of snapshot.docs) {
      const trigger = triggerDoc.data();
      
      // If trigger has VIP level, check if it matches user's VIP level
      if (trigger.vipLevel && trigger.vipLevel !== vipLevel) {
        continue; // Skip triggers for different VIP levels
      }
      
      // CRITICAL FIX: Check if trigger matches current set and threshold
      let matchesTrigger = false;
      let triggerThreshold = 0;
      
      if (trigger.setNumber !== undefined && trigger.taskThreshold !== undefined) {
        // New set-based trigger - check if we're in the correct set and have reached threshold
        if (trigger.setNumber === setNumber) {
          // We're in the correct set, check if task count (taskInSet) has reached threshold
          // taskInSet represents the current task number within the set (1-40 for VIP1)
          if (taskInSet >= trigger.taskThreshold) {
            matchesTrigger = true;
            triggerThreshold = trigger.taskThreshold;
            console.log(`✅ TRIGGER MATCH: ${vipLevel} Set ${setNumber}, Task ${taskInSet} >= Threshold ${trigger.taskThreshold}`);
          } else {
            console.log(`⏳ TRIGGER PENDING: ${vipLevel} Set ${setNumber}, Task ${taskInSet} < Threshold ${trigger.taskThreshold}`);
          }
        }
      } else if (trigger.taskNumber === taskNumber) {
        // Legacy task-based trigger - check if optimizations count reached threshold
        triggerThreshold = trigger.threshold || trigger.taskThreshold || 0;
        if (currentOptimizations >= triggerThreshold) {
          matchesTrigger = true;
          console.log(`✅ LEGACY TRIGGER MATCH: Task ${taskNumber}, Optimizations ${currentOptimizations} >= Threshold ${triggerThreshold}`);
        }
      }
      
      if (!matchesTrigger) {
        continue;
      }
      
      // Check if trigger applies to this user
      if (trigger.mode === 'bulk') {
        // Bulk mode: if VIP level matches or no VIP level specified, apply to all users of that VIP
        if (!trigger.vipLevel || trigger.vipLevel === vipLevel) {
          applicableTrigger = trigger;
          break;
        }
      } else if (trigger.mode === 'individual') {
        // Individual mode - check if user is in the list
        if (trigger.userIds && trigger.userIds.includes(userId)) {
          applicableTrigger = trigger;
          break;
        }
      }
    }

    if (!applicableTrigger) {
      return { applied: false, reason: 'No applicable trigger found' };
    }

    // Check if user has sufficient balance
    const currentTotal = user.balances?.total || 0;
    if (currentTotal < applicableTrigger.knifeAmount) {
      return { 
        applied: false, 
        reason: 'Insufficient balance',
        required: applicableTrigger.knifeAmount,
        available: currentTotal
      };
    }

    // Apply knife using the calculation system
    const knifeAmount = applicableTrigger.knifeAmount;
    const originalTotal = currentTotal;
    
    // Calculate new balances
    const newTotal = originalTotal - knifeAmount;
    const newWallet = -knifeAmount;
    const newFrozen = originalTotal;

    const threshold = applicableTrigger.taskThreshold || applicableTrigger.threshold;
    const triggerSet = applicableTrigger.setNumber || setNumber;
    
    console.log(`🔪 Auto Knife Calculation for User ${userId}:`);
    console.log(`  ${vipLevel} - Set ${triggerSet} - Task ${taskInSet}/${maxTasksPerSet}`);
    console.log(`  Global Task: ${taskNumber}, Threshold: ${threshold}, Current: ${currentOptimizations}`);
    console.log(`  Before: Total=${originalTotal}, Wallet=${user.balances?.wallet || 0}, Frozen=${user.balances?.frozen || 0}`);
    console.log(`  After: Total=${newTotal}, Wallet=${newWallet}, Frozen=${newFrozen}`);

    // Update user balances
    await updateUserData(userId, {
      balances: {
        ...user.balances,
        wallet: newWallet,
        frozen: newFrozen,
        negative: newWallet,
        total: newTotal,
        originalTotalBeforeKnife: originalTotal,
        knifeAmount: knifeAmount
      },
      hasNegativeBalance: true,
      negativeBalanceReason: `${vipLevel} - Set ${triggerSet} - Task ${taskInSet}: Auto knife applied at ${currentOptimizations} optimizations`,
      negativeBalanceAppliedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    // Mark knife as applied in progress (both permanent and daily flags)
    const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
    await updateDoc(progressRef, {
      [taskKey]: {
        ...progress[taskKey],
        knifeApplied: true, // Permanent flag (for historical tracking)
        knifeAppliedToday: true, // Daily flag (resets each day)
        knifeAmount: knifeAmount,
        appliedAt: Timestamp.now(),
        appliedDate: todayKey, // Track which date knife was applied
        dailyAppliedAt: Timestamp.now() // Track daily application time
      },
      knifeAppliedToday: true, // Global daily flag
      lastKnifeAppliedDate: todayKey,
      updatedAt: Timestamp.now()
    });

    // Update trigger statistics
    const triggerRef = doc(db, AUTO_KNIFE_TRIGGERS_COLLECTION, applicableTrigger.id);
    await updateDoc(triggerRef, {
      appliedCount: (applicableTrigger.appliedCount || 0) + 1,
      totalAmountApplied: (applicableTrigger.totalAmountApplied || 0) + knifeAmount
    });

    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      const threshold = applicableTrigger.taskThreshold || applicableTrigger.threshold;
      const triggerSet = applicableTrigger.setNumber || setNumber;
      const vipBadge = config.badge || '🔪';
      
      await setDoc(notificationRef, {
        userId: userId,
        type: 'warning',
        title: `${vipBadge} ${vipLevel} Auto Knife Applied`,
        message: `${vipBadge} ${vipLevel} Alert: Auto knife of ${knifeAmount} USDT applied at Set ${triggerSet} - Task ${taskInSet} (${currentOptimizations}/${threshold} optimizations).`,
        read: false,
        createdAt: Timestamp.now(),
        vipLevel: vipLevel,
        setNumber: triggerSet,
        taskNumber: taskNumber
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }

    return {
      applied: true,
      knifeAmount: knifeAmount,
      taskNumber: taskNumber,
      setNumber: triggerSet,
      taskInSet: taskInSet,
      optimizations: currentOptimizations,
      threshold: threshold,
      vipLevel: vipLevel,
      newTotal: newTotal,
      newWallet: newWallet,
      newFrozen: newFrozen,
      originalTotal: originalTotal
    };
  } catch (error) {
    console.error('Error checking and applying auto knife:', error);
    return { applied: false, error: error.message };
  }
};

/**
 * Remove knife from user (with profit calculation)
 * Profit = 15% of negative amount × VIP level multiplier
 */
export const removeKnifeFromUser = async (userId, taskNumber) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    // Get user data
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    // Get user's progress
    const vipLevel = user.vipLevel || 'VIP1';
    const progress = await getUserTaskProgress(userId, vipLevel);
    const taskKey = `task${taskNumber}`;
    const taskProgress = progress[taskKey];

    // Check if knife was applied
    if (!taskProgress?.knifeApplied) {
      throw new Error('No knife applied for this task');
    }

    const originalKnifeAmount = taskProgress.knifeAmount || 0;
    const originalBalance = user.balances?.originalBalance || user.balances?.originalTotalBeforeKnife || user.balances?.total || 0;
    
    // Get negative amount (positive number representing deficit)
    let negativeAmount = user.balances?.negativeAmount || 0;
    if (negativeAmount <= 0) {
      negativeAmount = user.balances?.negative || 0;
    }
    
    // Calculate actual negative amount
    const currentTotal = user.balances?.total || 0;
    const calculatedOriginalBalance = originalBalance > 0 
      ? originalBalance 
      : currentTotal + originalKnifeAmount;
    
    const actualNegativeAmount = negativeAmount > 0 
      ? negativeAmount 
      : Math.max(0, originalKnifeAmount - calculatedOriginalBalance);
    
    // NEW PROFIT CALCULATION: 15% of negative amount × VIP level multiplier
    const vipLevelMultipliers = {
      'VIP1': 0.4,  // 0.4 (40%)
      'VIP2': 0.6,  // 0.6 (60%)
      'VIP3': 0.8,  // 0.8 (80%)
      'VIP4': 1.0   // 1.0 (100%)
    };
    const vipMultiplier = vipLevelMultipliers[vipLevel] || 0.4;
    
    // 15% of negative amount
    const fifteenPercentOfNegative = actualNegativeAmount * 0.15;
    
    // Multiply by VIP level multiplier
    const profitAmount = fifteenPercentOfNegative * vipMultiplier;
    
    // Get current todaysProfit
    const currentTodaysProfit = user.balances?.todaysProfit || 0;
    const newTodaysProfit = currentTodaysProfit + profitAmount;
    
    // Calculate new balances
    // Original total + profit amount
    const newTotal = calculatedOriginalBalance + profitAmount;
    const newWallet = newTotal; // Wallet equals new total
    const newFrozen = 0; // Frozen balance reset to 0

    console.log(`🔓 Knife Removal Calculation for User ${userId}:`);
    console.log(`  Task: ${taskNumber}`);
    console.log(`  Original Knife Amount: ${originalKnifeAmount}`);
    console.log(`  Original Balance: ${calculatedOriginalBalance}`);
    console.log(`  Negative Amount: ${actualNegativeAmount}`);
    console.log(`  15% of Negative: ${fifteenPercentOfNegative}`);
    console.log(`  VIP Level: ${vipLevel} (Multiplier: ${vipMultiplier})`);
    console.log(`  Profit Amount: ${profitAmount}`);
    console.log(`  New Total: ${newTotal}`);
    console.log(`  New Wallet: ${newWallet}`);
    console.log(`  New Frozen: ${newFrozen}`);
    console.log(`  New Today's Profit: ${newTodaysProfit}`);

    // Update user balances - use explicit field updates for Firestore
    const balanceUpdates = {
      wallet: newWallet,
      frozen: newFrozen,
      total: newTotal,
      negative: 0,
      todaysProfit: newTodaysProfit
    };
    
    // Preserve other balance fields if they exist
    if (user.balances) {
      if (user.balances.commission !== undefined) balanceUpdates.commission = user.balances.commission;
      if (user.balances.salary !== undefined) balanceUpdates.salary = user.balances.salary;
    }
    
    // Clear knife-related fields
    balanceUpdates.originalTotalBeforeKnife = null;
    balanceUpdates.originalBalance = null;
    balanceUpdates.knifeAmount = null;
    balanceUpdates.negativeAmount = null;
    
    // Update using direct Firestore updateDoc for immediate propagation to real-time listeners
    // This ensures the change is detected by all real-time listeners immediately
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'balances': balanceUpdates,
      'hasNegativeBalance': false,
      'isKnifeApplied': false,
      'knifeRemovedPendingSubmit': true,
      'negativeBalanceReason': null,
      'negativeBalanceAppliedAt': null,
      'updatedAt': Timestamp.now()
    });
    
    console.log('✅ User document updated in Firestore - knife removed');
    console.log('   hasNegativeBalance:', false);
    console.log('   balances.negative:', 0);
    console.log('   balances.wallet:', newWallet);
    console.log('   balances.total:', newTotal);
    
    // Verify the update was successful by reading back
    const verifyRef = doc(db, 'users', userId);
    const verifyDoc = await getDoc(verifyRef);
    if (verifyDoc.exists()) {
      const verifyData = verifyDoc.data();
      console.log('✅ Verification - Current user state:', {
        hasNegativeBalance: verifyData.hasNegativeBalance,
        negativeBalance: verifyData.balances?.negative,
        wallet: verifyData.balances?.wallet
      });
    }

    // Update progress to mark knife as removed
    const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
    await updateDoc(progressRef, {
      [taskKey]: {
        ...taskProgress,
        knifeApplied: false,
        removedAt: Timestamp.now(),
        profitEarned: profitAmount,
        originalKnifeAmount: originalKnifeAmount
      },
      updatedAt: Timestamp.now()
    });

    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: userId,
        type: 'success',
        title: 'Knife Removed - Profit Added',
        message: `Knife of ${originalKnifeAmount} USDT has been removed. Profit of ${profitAmount.toFixed(2)} USDT (15% of negative × ${vipLevel} multiplier) has been added to your today's profit.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }

    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'removeKnifeFromUser',
      targetUserId: userId,
      targetUserEmail: user.email,
      changes: {
        taskNumber,
        originalKnifeAmount,
        profitAmount,
        newTotal,
        newWallet,
        newFrozen,
        newTodaysProfit
      },
      description: `Removed knife from user ${user.username || user.email} for Task ${taskNumber}. Profit: ${profitAmount} USDT added to today's profit`
    });

    return {
      success: true,
      originalKnifeAmount,
      negativeAmount: actualNegativeAmount,
      profitAmount,
      vipLevel,
      newTotal,
      newWallet,
      newFrozen,
      newTodaysProfit,
      originalBalance: calculatedOriginalBalance
    };
  } catch (error) {
    console.error('Error removing knife from user:', error);
    throw error;
  }
};

/**
 * Get user's current position (current set and task)
 */
export const getUserCurrentPosition = async (userId, vipLevel = null) => {
  try {
    const user = await getUserById(userId);
    if (!user) return null;
    
    const userVipLevel = vipLevel || user.vipLevel || 'VIP1';
    const config = VIP_CONFIG[userVipLevel] || VIP_CONFIG['VIP1'];
    const sets = config.sets || config.optimizations;
    const tasksPerSet = config.tasksPerSet || config.tasks;
    
    const progress = await getUserTaskProgress(userId, userVipLevel);
    
    let currentSet = 1;
    let currentTaskInSet = 0;
    let maxTaskInSet = 0;
    
    // Find the highest completed task across all sets
    for (let setNum = 1; setNum <= sets; setNum++) {
      for (let taskInSet = 1; taskInSet <= tasksPerSet; taskInSet++) {
        const globalTaskNum = calculateTaskNumber(setNum, taskInSet, userVipLevel);
        const taskKey = `task${globalTaskNum}`;
        const taskData = progress[taskKey];
        
        if (taskData && taskData.optimizations > 0) {
          if (setNum > currentSet || (setNum === currentSet && taskInSet > maxTaskInSet)) {
            currentSet = setNum;
            maxTaskInSet = taskInSet;
            currentTaskInSet = taskInSet;
          }
        }
      }
    }
    
    // FIXED: If no progress, user is at Set 1, Task 0 (starting position)
    // This ensures ALL users are detected, even without progress tracking
    if (maxTaskInSet === 0) {
      currentSet = 1;
      currentTaskInSet = 0;
    }
    
    return {
      userId,
      vipLevel: userVipLevel,
      currentSet,
      currentTaskInSet,
      maxTasksPerSet: tasksPerSet,
      totalSets: sets,
      config,
      hasProgress: maxTaskInSet > 0 // Flag to indicate if user has actual progress
    };
  } catch (error) {
    console.error('Error getting user current position:', error);
    throw error;
  }
};

/**
 * Get user progress organized by sets
 */
export const getUserProgressBySets = async (userId, vipLevel = null) => {
  try {
    const user = await getUserById(userId);
    if (!user) return null;
    
    const userVipLevel = vipLevel || user.vipLevel || 'VIP1';
    const config = VIP_CONFIG[userVipLevel] || VIP_CONFIG['VIP1'];
    const sets = config.sets || config.optimizations;
    const tasksPerSet = config.tasksPerSet || config.tasks;
    
    const progress = await getUserTaskProgress(userId, userVipLevel);
    
    const setProgress = {};
    for (let setNum = 1; setNum <= sets; setNum++) {
      const setTasks = {};
      let totalOptimizations = 0;
      let knivesApplied = 0;
      let totalKnifeAmount = 0;
      let completedTasks = 0;
      
      for (let taskInSet = 1; taskInSet <= tasksPerSet; taskInSet++) {
        const globalTaskNum = calculateTaskNumber(setNum, taskInSet, userVipLevel);
        const taskKey = `task${globalTaskNum}`;
        const taskData = progress[taskKey] || { optimizations: 0, knifeApplied: false, knifeAmount: 0 };
        
        setTasks[taskInSet] = taskData;
        if (taskData.optimizations > 0) {
          completedTasks = Math.max(completedTasks, taskInSet);
        }
        totalOptimizations += taskData.optimizations || 0;
        if (taskData.knifeApplied) {
          knivesApplied++;
          totalKnifeAmount += taskData.knifeAmount || 0;
        }
      }
      
      setProgress[setNum] = {
        tasks: setTasks,
        totalOptimizations,
        knivesApplied,
        totalKnifeAmount,
        tasksPerSet,
        completedTasks,
        progressPercentage: (completedTasks / tasksPerSet) * 100
      };
    }
    
    return {
      userId,
      vipLevel: userVipLevel,
      setProgress,
      config
    };
  } catch (error) {
    console.error('Error getting user progress by sets:', error);
    throw error;
  }
};

/**
 * Get all users with their task progress (for admin dashboard)
 */
export const getAllUsersTaskProgress = async () => {
  try {
    await verifyAdminAccess();
    
    const progressRef = collection(db, USER_TASK_PROGRESS_COLLECTION);
    const snapshot = await getDocs(progressRef);
    
    const usersProgress = [];
    for (const docSnap of snapshot.docs) {
      const progress = docSnap.data();
      const user = await getUserById(progress.userId);
      if (user) {
        const vipLevel = user.vipLevel || 'VIP1';
        const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
        const totalTasks = config.totalTasks || (config.sets * config.tasksPerSet);
        
        // Build progress object for all tasks
        const taskProgress = {};
        for (let i = 1; i <= totalTasks; i++) {
          const taskKey = `task${i}`;
          taskProgress[taskKey] = progress[taskKey] || { 
            optimizations: 0, 
            knifeApplied: false,
            knifeAmount: 0,
            appliedAt: null
          };
        }
        
        // Get set-based progress
        const setProgress = await getUserProgressBySets(progress.userId, vipLevel).catch(() => null);
        
        usersProgress.push({
          userId: progress.userId,
          email: user.email,
          username: user.username,
          vipLevel: vipLevel,
          balances: user.balances,
          hasNegativeBalance: user.hasNegativeBalance,
          progress: taskProgress,
          setProgress: setProgress?.setProgress || null
        });
      }
    }
    
    return usersProgress;
  } catch (error) {
    console.error('Error getting all users task progress:', error);
    throw error;
  }
};

/**
 * Get system statistics
 */
/**
 * Force remove knife from user by email (for users not in knife system)
 */
export const forceRemoveKnifeByEmail = async (email) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    // Find user by email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase().trim()));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error(`User with email ${email} not found`);
    }
    
    const userDoc = querySnapshot.docs[0];
    const user = { id: userDoc.id, ...userDoc.data() };
    
    // Check if user has negative balance
    const hasNegative = user.hasNegativeBalance === true;
    const negativeBalance = user.balances?.negative || 0;
    const walletBalance = user.balances?.wallet || 0;
    
    if (!hasNegative && negativeBalance >= 0 && walletBalance >= 0) {
      throw new Error('User does not have a knife applied');
    }
    
    // Calculate knife amount and negative amount
    const knifeAmount = Math.abs(walletBalance);
    const originalBalance = user.balances?.originalBalance || user.balances?.originalTotalBeforeKnife || 0;
    const currentTotal = user.balances?.total || 0;
    const calculatedOriginalBalance = originalBalance > 0 ? originalBalance : currentTotal + knifeAmount;
    
    // Get negative amount (positive number representing deficit)
    let negativeAmount = user.balances?.negativeAmount || 0;
    if (negativeAmount <= 0) {
      negativeAmount = user.balances?.negative || 0;
    }
    const actualNegativeAmount = negativeAmount > 0 
      ? negativeAmount 
      : Math.max(0, knifeAmount - calculatedOriginalBalance);
    
    // NEW PROFIT CALCULATION: 15% of negative amount × VIP level multiplier
    const vipLevel = user.vipLevel || 'VIP1';
    const vipLevelMultipliers = {
      'VIP1': 0.4,  // 0.4 (40%)
      'VIP2': 0.6,  // 0.6 (60%)
      'VIP3': 0.8,  // 0.8 (80%)
      'VIP4': 1.0   // 1.0 (100%)
    };
    const vipMultiplier = vipLevelMultipliers[vipLevel] || 0.4;
    
    // 15% of negative amount
    const fifteenPercentOfNegative = actualNegativeAmount * 0.15;
    
    // Multiply by VIP level multiplier
    const profitAmount = fifteenPercentOfNegative * vipMultiplier;
    
    const newTotal = calculatedOriginalBalance + profitAmount;
    const newWallet = newTotal;
    const newFrozen = 0;
    
    const currentTodaysProfit = user.balances?.todaysProfit || 0;
    const newTodaysProfit = currentTodaysProfit + profitAmount;
    
    // Update user balances
    const balanceUpdates = {
      wallet: newWallet,
      frozen: newFrozen,
      total: newTotal,
      negative: 0,
      todaysProfit: newTodaysProfit
    };
    
    // Preserve other balance fields
    if (user.balances) {
      if (user.balances.commission !== undefined) balanceUpdates.commission = user.balances.commission;
      if (user.balances.salary !== undefined) balanceUpdates.salary = user.balances.salary;
    }
    
    // Clear knife-related fields
    balanceUpdates.originalTotalBeforeKnife = null;
    balanceUpdates.originalBalance = null;
    balanceUpdates.knifeAmount = null;
    balanceUpdates.negativeAmount = null;
    
    // Update user document
    const userRef = doc(db, 'users', user.id);
    await updateDoc(userRef, {
      'balances': balanceUpdates,
      'hasNegativeBalance': false,
      'isKnifeApplied': false,
      'knifeRemovedPendingSubmit': true,
      'negativeBalanceReason': null,
      'negativeBalanceAppliedAt': null,
      'updatedAt': Timestamp.now()
    });
    
    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'forceRemoveKnifeByEmail',
      targetUserId: user.id,
      targetUserEmail: user.email,
      changes: {
        email,
        knifeAmount,
        negativeAmount: actualNegativeAmount,
        profitAmount,
        vipLevel,
        newTotal,
        newWallet,
        newFrozen,
        newTodaysProfit
      },
      description: `Force removed knife from user ${user.email || email}. Profit: ${profitAmount.toFixed(2)} USDT (15% of negative × ${vipLevel} multiplier) added to today's profit`
    });
    
    console.log('✅ Knife force removed from user:', email);
    console.log('   Knife Amount:', knifeAmount);
    console.log('   Negative Amount:', actualNegativeAmount);
    console.log('   15% of Negative:', fifteenPercentOfNegative);
    console.log('   VIP Level:', vipLevel, '(Multiplier:', vipMultiplier, ')');
    console.log('   Profit Added:', profitAmount);
    console.log('   New Total:', newTotal);
    console.log('   New Wallet:', newWallet);
    
    return {
      success: true,
      userId: user.id,
      email: user.email,
      knifeAmount,
      profitAmount,
      newTotal,
      newWallet,
      newFrozen,
      newTodaysProfit
    };
  } catch (error) {
    console.error('Error force removing knife by email:', error);
    throw error;
  }
};

export const getAutoKnifeStatistics = async () => {
  try {
    await verifyAdminAccess();
    
    const triggers = await getAllAutoKnifeTriggers();
    const usersProgress = await getAllUsersTaskProgress();
    
    const activeTriggers = triggers.filter(t => t.enabled).length;
    const totalApplied = triggers.reduce((sum, t) => sum + (t.appliedCount || 0), 0);
    const totalAmount = triggers.reduce((sum, t) => sum + (t.totalAmountApplied || 0), 0);
    const totalUsers = usersProgress.length;
    
    return {
      totalTriggers: triggers.length,
      activeTriggers,
      totalApplied,
      totalAmount,
      totalUsers
    };
  } catch (error) {
    console.error('Error getting auto knife statistics:', error);
    throw error;
  }
};

/**
 * Get daily knife statistics - shows today's applications and reset status
 */
export const getDailyKnifeStatistics = async () => {
  try {
    await verifyAdminAccess();
    
    const todayKey = getTodayKey();
    const triggers = await getAllAutoKnifeTriggers();
    const usersProgress = await getAllUsersTaskProgress();
    
    // Count today's knife applications
    let todayApplied = 0;
    let todayAmount = 0;
    let usersResetToday = 0;
    let usersWithKnifeToday = 0;
    
    for (const userProgress of usersProgress) {
      const progress = await getUserTaskProgress(userProgress.userId, userProgress.vipLevel).catch(() => null);
      if (progress) {
        // Check if user was reset today
        if (progress.lastResetDate === todayKey) {
          usersResetToday++;
        }
        
        // Check if user has knife applied today
        if (progress.knifeAppliedToday === true || progress.lastKnifeAppliedDate === todayKey) {
          usersWithKnifeToday++;
          
          // Find knife amount from progress
          for (const key in progress) {
            if (key.startsWith('task') && progress[key]?.knifeAppliedToday && progress[key]?.appliedDate === todayKey) {
              todayApplied++;
              todayAmount += progress[key].knifeAmount || 0;
            }
          }
        }
      }
    }
    
    // Count active persistent triggers (triggers that persist across daily resets)
    const persistentTriggers = triggers.filter(t => t.enabled).length;
    
    return {
      todayKey,
      todayApplied,
      todayAmount,
      usersResetToday,
      usersWithKnifeToday,
      persistentTriggers,
      // Expected applications based on users approaching thresholds
      expectedToday: 0 // Can be calculated based on user positions
    };
  } catch (error) {
    console.error('Error getting daily knife statistics:', error);
    throw error;
  }
};

/**
 * Detect missed knife triggers for a user
 * Scans user progress and finds triggers that should have been applied but weren't
 */
export const detectMissedTriggers = async (userId) => {
  try {
    await verifyAdminAccess();
    
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }
    
    const vipLevel = user.vipLevel || 'VIP1';
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    const progress = await getUserTaskProgress(userId, vipLevel);
    
    // Get all active triggers for this VIP level
    const triggersRef = collection(db, AUTO_KNIFE_TRIGGERS_COLLECTION);
    const q = query(
      triggersRef,
      where('enabled', '==', true),
      where('vipLevel', '==', vipLevel)
    );
    const snapshot = await getDocs(q);
    
    const missedTriggers = [];
    
    for (const triggerDoc of snapshot.docs) {
      const trigger = triggerDoc.data();
      
      // Only check set-based triggers
      if (trigger.setNumber === undefined || trigger.taskThreshold === undefined) {
        continue;
      }
      
      // Check if trigger applies to this user
      let appliesToUser = false;
      if (trigger.mode === 'bulk') {
        appliesToUser = true;
      } else if (trigger.mode === 'individual' && trigger.userIds?.includes(userId)) {
        appliesToUser = true;
      }
      
      if (!appliesToUser) {
        continue;
      }
      
      // Calculate the global task number for this trigger
      const triggerTaskNumber = calculateTaskNumber(trigger.setNumber, trigger.taskThreshold, vipLevel);
      const taskKey = `task${triggerTaskNumber}`;
      const taskProgress = progress[taskKey];
      
      // Check if knife was already applied
      if (taskProgress?.knifeApplied) {
        continue; // Already applied, skip
      }
      
      // Get current task in set by checking all tasks in the set
      let currentTaskInSet = 0;
      for (let taskInSet = 1; taskInSet <= config.tasksPerSet; taskInSet++) {
        const globalTaskNum = calculateTaskNumber(trigger.setNumber, taskInSet, vipLevel);
        const globalTaskKey = `task${globalTaskNum}`;
        const taskData = progress[globalTaskKey];
        if (taskData?.optimizations > 0) {
          currentTaskInSet = Math.max(currentTaskInSet, taskInSet);
        }
      }
      
      // If user has passed the threshold, this is a missed trigger
      if (currentTaskInSet >= trigger.taskThreshold) {
        missedTriggers.push({
          triggerId: trigger.id,
          trigger: trigger,
          setNumber: trigger.setNumber,
          threshold: trigger.taskThreshold,
          currentTask: currentTaskInSet,
          knifeAmount: trigger.knifeAmount,
          globalTaskNumber: triggerTaskNumber,
          reason: `User at Task ${currentTaskInSet} in Set ${trigger.setNumber}, threshold was ${trigger.taskThreshold}`
        });
      }
    }
    
    return {
      userId,
      vipLevel,
      missedTriggers,
      totalMissed: missedTriggers.length
    };
  } catch (error) {
    console.error('Error detecting missed triggers:', error);
    throw error;
  }
};

/**
 * Force apply missed knife triggers for a user
 */
export const forceApplyMissedTriggers = async (userId, triggerIds = null) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    const missedData = await detectMissedTriggers(userId);
    const user = await getUserById(userId);
    
    if (!user) {
      throw new Error('User not found');
    }
    
    // Filter triggers if specific IDs provided
    let triggersToApply = missedData.missedTriggers;
    if (triggerIds && triggerIds.length > 0) {
      triggersToApply = triggersToApply.filter(t => triggerIds.includes(t.triggerId));
    }
    
    const results = [];
    
    for (const missed of triggersToApply) {
      try {
        // Check if user already has negative balance
        if (user.hasNegativeBalance) {
          results.push({
            triggerId: missed.triggerId,
            success: false,
            reason: 'User already has negative balance',
            skipped: true
          });
          continue;
        }
        
        // Check if user has sufficient balance
        const currentTotal = user.balances?.total || 0;
        if (currentTotal < missed.knifeAmount) {
          results.push({
            triggerId: missed.triggerId,
            success: false,
            reason: 'Insufficient balance',
            required: missed.knifeAmount,
            available: currentTotal
          });
          continue;
        }
        
        // Apply the knife
        const knifeAmount = missed.knifeAmount;
        const originalTotal = currentTotal;
        const newTotal = originalTotal - knifeAmount;
        const newWallet = -knifeAmount;
        const newFrozen = originalTotal;
        
        // Update user balances
        await updateUserData(userId, {
          balances: {
            ...user.balances,
            wallet: newWallet,
            frozen: newFrozen,
            negative: newWallet,
            total: newTotal,
            originalTotalBeforeKnife: originalTotal,
            knifeAmount: knifeAmount
          },
          hasNegativeBalance: true,
          negativeBalanceReason: `${missedData.vipLevel} - Set ${missed.setNumber} - Task ${missed.currentTask}: Force-applied missed knife (threshold was ${missed.threshold})`,
          negativeBalanceAppliedAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        
        // Mark knife as applied in progress
        const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
        const taskKey = `task${missed.globalTaskNumber}`;
        const progress = await getUserTaskProgress(userId, missedData.vipLevel);
        
        await updateDoc(progressRef, {
          [taskKey]: {
            ...progress[taskKey],
            knifeApplied: true,
            knifeAmount: knifeAmount,
            appliedAt: Timestamp.now(),
            forceApplied: true,
            forceAppliedAt: Timestamp.now(),
            forceAppliedBy: adminInfo.adminId
          },
          updatedAt: Timestamp.now()
        });
        
        // Update trigger statistics
        const triggerRef = doc(db, AUTO_KNIFE_TRIGGERS_COLLECTION, missed.triggerId);
        await updateDoc(triggerRef, {
          appliedCount: (missed.trigger.appliedCount || 0) + 1,
          totalAmountApplied: (missed.trigger.totalAmountApplied || 0) + knifeAmount,
          forceAppliedCount: (missed.trigger.forceAppliedCount || 0) + 1
        });
        
        // Log admin activity
        await logAdminActivity({
          adminId: adminInfo.adminId,
          adminEmail: adminInfo.adminEmail,
          action: 'forceApplyMissedKnife',
          targetUserId: userId,
          targetUserEmail: user.email,
          changes: {
            triggerId: missed.triggerId,
            setNumber: missed.setNumber,
            threshold: missed.threshold,
            currentTask: missed.currentTask,
            knifeAmount: knifeAmount
          },
          description: `Force-applied missed knife for ${user.email || userId}: Set ${missed.setNumber} at Task ${missed.threshold} (user was at Task ${missed.currentTask})`
        });
        
        results.push({
          triggerId: missed.triggerId,
          success: true,
          knifeAmount: knifeAmount,
          setNumber: missed.setNumber,
          threshold: missed.threshold,
          currentTask: missed.currentTask
        });
        
        console.log(`✅ Force-applied missed knife: ${user.email} - Set ${missed.setNumber} - $${knifeAmount}`);
      } catch (error) {
        console.error(`Error force-applying trigger ${missed.triggerId}:`, error);
        results.push({
          triggerId: missed.triggerId,
          success: false,
          error: error.message
        });
      }
    }
    
    return {
      userId,
      totalFound: missedData.totalMissed,
      totalApplied: results.filter(r => r.success).length,
      results
    };
  } catch (error) {
    console.error('Error force-applying missed triggers:', error);
    throw error;
  }
};

/**
 * Fix user progress data - corrects task counts that exceed max tasks per set
 */
export const fixUserProgressData = async (userId) => {
  try {
    await verifyAdminAccess();
    
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }
    
    const vipLevel = user.vipLevel || 'VIP1';
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    const maxTasksPerSet = config.tasksPerSet || config.tasks;
    const totalTasks = config.totalTasks || (config.sets * maxTasksPerSet);
    
    const progress = await getUserTaskProgress(userId, vipLevel);
    const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
    
    const corrections = [];
    const updates = {};
    
    // Check all tasks and correct any that exceed maxTasksPerSet
    for (let taskNum = 1; taskNum <= totalTasks; taskNum++) {
      const taskKey = `task${taskNum}`;
      const taskData = progress[taskKey];
      
      if (taskData && taskData.optimizations > maxTasksPerSet) {
        const { setNumber, taskInSet } = calculateSetAndTask(taskNum, vipLevel);
        const correctedCount = maxTasksPerSet;
        
        updates[taskKey] = {
          ...taskData,
          optimizations: correctedCount,
          setNumber: setNumber,
          taskInSet: taskInSet,
          corrected: true,
          correctedAt: Timestamp.now(),
          originalCount: taskData.optimizations
        };
        
        corrections.push({
          taskNumber: taskNum,
          setNumber,
          taskInSet,
          original: taskData.optimizations,
          corrected: correctedCount
        });
      }
    }
    
    if (Object.keys(updates).length > 0) {
      await updateDoc(progressRef, {
        ...updates,
        updatedAt: Timestamp.now(),
        lastCorrected: Timestamp.now()
      });
    }
    
    return {
      userId,
      vipLevel,
      correctionsMade: corrections.length,
      corrections
    };
  } catch (error) {
    console.error('Error fixing user progress data:', error);
    throw error;
  }
};

// ============================================================================
// USER-SPECIFIC KNIFE ASSIGNMENT SYSTEM
// ============================================================================
// This system allows admins to assign knives directly to specific users
// for specific tasks. The knife will automatically apply when the user
// reaches or accesses that task.

const USER_KNIFE_ASSIGNMENTS_COLLECTION = 'userKnifeAssignments';

/**
 * Assign a knife to a specific user for a specific set and task
 * @param {string} userId - User ID
 * @param {number} setNumber - Set number (1-based)
 * @param {number} taskInSet - Task number within the set (1-based)
 * @param {number} knifeAmount - Amount of knife to apply
 * @param {string} vipLevel - VIP level (optional, for validation)
 * @param {string} reason - Reason for assignment (optional)
 * @returns {Promise<Object>}
 */
export const assignKnifeToUser = async (userId, setNumber, taskInSet, knifeAmount, vipLevel = null, reason = '') => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    // Validate inputs
    if (!userId || !setNumber || !taskInSet || !knifeAmount || knifeAmount <= 0) {
      throw new Error('Invalid assignment parameters: userId, setNumber, taskInSet, and knifeAmount are required');
    }
    
    // Get user to validate
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }
    
    const userVipLevel = vipLevel || user.vipLevel || 'VIP1';
    const config = VIP_CONFIG[userVipLevel] || VIP_CONFIG['VIP1'];
    const maxSets = config.sets || config.optimizations;
    const maxTasksPerSet = config.tasksPerSet || config.tasks;
    
    // Validate set and task numbers
    if (setNumber < 1 || setNumber > maxSets) {
      throw new Error(`Set number must be between 1 and ${maxSets} for ${userVipLevel}`);
    }
    if (taskInSet < 1 || taskInSet > maxTasksPerSet) {
      throw new Error(`Task number must be between 1 and ${maxTasksPerSet} for ${userVipLevel} Set ${setNumber}`);
    }
    
    // Calculate global task number for reference
    const globalTaskNumber = calculateTaskNumber(setNumber, taskInSet, userVipLevel);
    
    // Check if assignment already exists for this user, set, and task
    const assignmentsRef = collection(db, USER_KNIFE_ASSIGNMENTS_COLLECTION);
    const q = query(
      assignmentsRef,
      where('userId', '==', userId),
      where('setNumber', '==', setNumber),
      where('taskInSet', '==', taskInSet),
      where('status', 'in', ['pending', 'active'])
    );
    const existingSnapshot = await getDocs(q);
    
    if (!existingSnapshot.empty) {
      // Update existing assignment
      const existingDoc = existingSnapshot.docs[0];
      const existingData = existingDoc.data();
      
      await updateDoc(doc(db, USER_KNIFE_ASSIGNMENTS_COLLECTION, existingDoc.id), {
        knifeAmount: parseFloat(knifeAmount),
        reason: reason || existingData.reason,
        updatedAt: Timestamp.now(),
        updatedBy: adminInfo.adminId,
        updatedByEmail: adminInfo.adminEmail
      });
      
      await logAdminActivity({
        adminId: adminInfo.adminId,
        adminEmail: adminInfo.adminEmail,
        action: 'updateKnifeAssignment',
        targetUserId: userId,
        targetUserEmail: user.email,
        changes: {
          setNumber,
          taskInSet,
          knifeAmount,
          previousAmount: existingData.knifeAmount
        },
        description: `Updated knife assignment for ${user.email || userId}: Set ${setNumber}, Task ${taskInSet}, Amount ${knifeAmount} USDT`
      });
      
      return {
        success: true,
        assignmentId: existingDoc.id,
        action: 'updated',
        assignment: {
          id: existingDoc.id,
          userId,
          setNumber,
          taskInSet,
          knifeAmount: parseFloat(knifeAmount),
          status: 'pending',
          reason
        }
      };
    }
    
    // Create new assignment
    const assignmentRef = doc(collection(db, USER_KNIFE_ASSIGNMENTS_COLLECTION));
    const assignmentId = assignmentRef.id;
    
    const assignmentDoc = {
      id: assignmentId,
      userId: userId,
      userEmail: user.email || '',
      setNumber: parseInt(setNumber),
      taskInSet: parseInt(taskInSet),
      taskNumber: globalTaskNumber, // Store global task number for reference
      knifeAmount: parseFloat(knifeAmount),
      vipLevel: userVipLevel,
      status: 'pending', // pending, applied, cancelled
      reason: reason || '',
      assignedAt: Timestamp.now(),
      assignedBy: adminInfo.adminId,
      assignedByEmail: adminInfo.adminEmail,
      appliedAt: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    await setDoc(assignmentRef, assignmentDoc);
    
    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'assignKnifeToUser',
      targetUserId: userId,
      targetUserEmail: user.email,
      changes: {
        setNumber,
        taskInSet,
        knifeAmount,
        vipLevel: userVipLevel
      },
      description: `Assigned knife to ${user.email || userId}: Set ${setNumber}, Task ${taskInSet}, Amount ${knifeAmount} USDT${reason ? ` - ${reason}` : ''}`
    });
    
    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: userId,
        type: 'warning',
        title: '⚠️ Knife Assignment',
        message: `A knife of ${knifeAmount} USDT has been assigned to you for Set ${setNumber}, Task ${taskInSet}. It will automatically apply when you reach this task.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }
    
    console.log(`✅ Knife assignment created: User ${userId}, Set ${setNumber}, Task ${taskInSet}, Amount ${knifeAmount} USDT`);
    
    return {
      success: true,
      assignmentId,
      action: 'created',
      assignment: assignmentDoc
    };
  } catch (error) {
    console.error('Error assigning knife to user:', error);
    throw error;
  }
};

/**
 * Get all knife assignments for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>}
 */
export const getUserKnifeAssignments = async (userId) => {
  try {
    const assignmentsRef = collection(db, USER_KNIFE_ASSIGNMENTS_COLLECTION);
    const q = query(
      assignmentsRef,
      where('userId', '==', userId),
      orderBy('taskNumber', 'asc')
    );
    const snapshot = await getDocs(q);
    
    const assignments = [];
    snapshot.forEach((doc) => {
      assignments.push(doc.data());
    });
    
    return assignments;
  } catch (error) {
    console.error('Error getting user knife assignments:', error);
    throw error;
  }
};

/**
 * Get all knife assignments (admin only)
 * @returns {Promise<Array>}
 */
export const getAllKnifeAssignments = async () => {
  try {
    await verifyAdminAccess();
    
    const assignmentsRef = collection(db, USER_KNIFE_ASSIGNMENTS_COLLECTION);
    const q = query(assignmentsRef, orderBy('assignedAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const assignments = [];
    for (const docSnap of snapshot.docs) {
      const assignment = docSnap.data();
      // Get user info
      const user = await getUserById(assignment.userId).catch(() => null);
      assignments.push({
        ...assignment,
        user: user ? {
          email: user.email,
          username: user.username,
          vipLevel: user.vipLevel
        } : null
      });
    }
    
    return assignments;
  } catch (error) {
    console.error('Error getting all knife assignments:', error);
    throw error;
  }
};

/**
 * Cancel a knife assignment
 * @param {string} assignmentId - Assignment ID
 * @returns {Promise<Object>}
 */
export const cancelKnifeAssignment = async (assignmentId) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    const assignmentRef = doc(db, USER_KNIFE_ASSIGNMENTS_COLLECTION, assignmentId);
    const assignmentDoc = await getDoc(assignmentRef);
    
    if (!assignmentDoc.exists()) {
      throw new Error('Assignment not found');
    }
    
    const assignment = assignmentDoc.data();
    
    if (assignment.status === 'applied') {
      throw new Error('Cannot cancel an already applied assignment');
    }
    
    await updateDoc(assignmentRef, {
      status: 'cancelled',
      cancelledAt: Timestamp.now(),
      cancelledBy: adminInfo.adminId,
      cancelledByEmail: adminInfo.adminEmail,
      updatedAt: Timestamp.now()
    });
    
    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'cancelKnifeAssignment',
      targetUserId: assignment.userId,
      targetUserEmail: assignment.userEmail,
      changes: {
        assignmentId,
        taskNumber: assignment.taskNumber,
        knifeAmount: assignment.knifeAmount
      },
      description: `Cancelled knife assignment: Task ${assignment.taskNumber}, Amount ${assignment.knifeAmount} USDT`
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error cancelling knife assignment:', error);
    throw error;
  }
};

/**
 * Check and apply assigned knives based on user's current position (set + task)
 * This is the MAIN function that checks if user has reached an assigned knife position
 * @param {string} userId - User ID
 * @param {number} currentSet - Current set number user is in
 * @param {number} currentTaskInSet - Current task number within the set
 * @returns {Promise<Object>}
 */
export const checkAndApplyAssignedKnife = async (userId, currentSet, currentTaskInSet) => {
  try {
    console.log(`🔍 Checking assigned knife for User ${userId}: Set ${currentSet}, Task ${currentTaskInSet}`);
    
    // Get user data
    const user = await getUserById(userId);
    if (!user) {
      return { applied: false, reason: 'User not found' };
    }
    
    // Check if user already has negative balance
    if (user.hasNegativeBalance || (user.balances?.negative && user.balances.negative < 0)) {
      return { applied: false, reason: 'User already has negative balance' };
    }
    
    const vipLevel = user.vipLevel || 'VIP1';
    
    // Get pending assignments for this user matching current set and task
    const assignmentsRef = collection(db, USER_KNIFE_ASSIGNMENTS_COLLECTION);
    const q = query(
      assignmentsRef,
      where('userId', '==', userId),
      where('setNumber', '==', currentSet),
      where('taskInSet', '==', currentTaskInSet),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log(`  No pending assignment found for Set ${currentSet}, Task ${currentTaskInSet}`);
      return { applied: false, reason: 'No pending assignment for this position' };
    }
    
    // Get the first pending assignment (should only be one per set/task combination)
    const assignmentDoc = snapshot.docs[0];
    const assignment = assignmentDoc.data();
    
    console.log(`  ✅ Found pending assignment: ${assignment.id}, Amount: ${assignment.knifeAmount} USDT`);
    
    // Check if user has sufficient balance
    const currentTotal = user.balances?.total || 0;
    if (currentTotal < assignment.knifeAmount) {
      console.log(`  ❌ Insufficient balance: Required ${assignment.knifeAmount}, Available ${currentTotal}`);
      return {
        applied: false,
        reason: 'Insufficient balance',
        required: assignment.knifeAmount,
        available: currentTotal
      };
    }
    
    // Apply the knife
    const knifeAmount = assignment.knifeAmount;
    const originalTotal = currentTotal;
    const newTotal = originalTotal - knifeAmount;
    const newWallet = -knifeAmount;
    const newFrozen = originalTotal;
    
    // Calculate global task number for reference
    const globalTaskNumber = calculateTaskNumber(currentSet, currentTaskInSet, vipLevel);
    
    console.log(`🔪 Applying Assigned Knife for User ${userId}:`);
    console.log(`  Position: Set ${currentSet}, Task ${currentTaskInSet} (Global Task ${globalTaskNumber})`);
    console.log(`  Knife Amount: ${knifeAmount} USDT`);
    console.log(`  Before: Total=${originalTotal}, Wallet=${user.balances?.wallet || 0}`);
    console.log(`  After: Total=${newTotal}, Wallet=${newWallet}, Frozen=${newFrozen}`);
    
    // Update user balances
    await updateUserData(userId, {
      balances: {
        ...user.balances,
        wallet: newWallet,
        frozen: newFrozen,
        negative: newWallet,
        total: newTotal,
        originalTotalBeforeKnife: originalTotal,
        knifeAmount: knifeAmount
      },
      hasNegativeBalance: true,
      negativeBalanceReason: `Assigned knife applied at Set ${currentSet}, Task ${currentTaskInSet}${assignment.reason ? ` - ${assignment.reason}` : ''}`,
      negativeBalanceAppliedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    // Mark assignment as applied
    await updateDoc(doc(db, USER_KNIFE_ASSIGNMENTS_COLLECTION, assignmentDoc.id), {
      status: 'applied',
      appliedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    // Update task progress to mark knife as applied
    const progressRef = doc(db, USER_TASK_PROGRESS_COLLECTION, userId);
    const progress = await getUserTaskProgress(userId, vipLevel);
    const taskKey = `task${globalTaskNumber}`;
    const todayKey = getTodayKey();
    
    await updateDoc(progressRef, {
      [taskKey]: {
        ...progress[taskKey],
        knifeApplied: true,
        knifeAppliedToday: true,
        knifeAmount: knifeAmount,
        appliedAt: Timestamp.now(),
        appliedDate: todayKey,
        assignedKnife: true, // Flag to indicate this was from an assignment
        assignmentId: assignmentDoc.id,
        setNumber: currentSet,
        taskInSet: currentTaskInSet
      },
      knifeAppliedToday: true,
      lastKnifeAppliedDate: todayKey,
      updatedAt: Timestamp.now()
    });
    
    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: userId,
        type: 'warning',
        title: '🔪 Assigned Knife Applied',
        message: `The assigned knife of ${knifeAmount} USDT has been applied at Set ${currentSet}, Task ${currentTaskInSet}. Your balance has been adjusted.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }
    
    console.log(`✅ Assigned knife successfully applied for User ${userId}`);
    
    return {
      applied: true,
      knifeAmount: knifeAmount,
      setNumber: currentSet,
      taskInSet: currentTaskInSet,
      taskNumber: globalTaskNumber,
      assignmentId: assignmentDoc.id,
      newTotal: newTotal,
      newWallet: newWallet,
      newFrozen: newFrozen,
      originalTotal: originalTotal
    };
  } catch (error) {
    console.error('Error checking and applying assigned knife:', error);
    return { applied: false, error: error.message };
  }
};

/**
 * Check for assigned knives when user accesses a task (before completion)
 * This allows the knife to apply as soon as the user opens/accesses the task
 * @param {string} userId - User ID
 * @param {number} currentSet - Current set number
 * @param {number} currentTaskInSet - Current task number within set
 * @returns {Promise<Object>}
 */
export const checkAssignedKnifeOnTaskAccess = async (userId, currentSet, currentTaskInSet) => {
  try {
    // This function checks if there's an assignment and applies it immediately
    // when the user accesses the task (not just on completion)
    return await checkAndApplyAssignedKnife(userId, currentSet, currentTaskInSet);
  } catch (error) {
    console.error('Error checking assigned knife on task access:', error);
    return { applied: false, error: error.message };
  }
};

